import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEventHandler
} from "react";

import type { CaptionDisplay } from "@/audio/captions";
import type {
  ActivityAdapter,
  ActivityCompletionMetrics,
  ActivityExecutionContext
} from "@/app/integration/contracts";
import type { EventBridge } from "@/game/core";
import type { InputMethod } from "@/game/input";
import {
  getActivationInputMethod,
  usePrefersReducedMotion
} from "@/platform/accessibility";

import type {
  ActivityActivationProps,
  ActivityHostActionCreators,
  ActivityHostBridgeErrorEvent,
  ActivityHostBridgeEvents,
  ActivityHostCommand,
  ActivityHostCompletionMetrics,
  ActivityHostFeedback,
  ActivityHostInstruction,
  ActivityHostLevelError,
  ActivityHostPhase,
  ActivityHostReadyEvent
} from "./contracts";

type ActivePhase = Exclude<ActivityHostPhase, "level-error">;

interface ActivitySession<TPayload, TState, TRenderModel> {
  readonly accumulatedActiveMs: number;
  readonly activeSinceMs: number | null;
  readonly attemptNumber: number;
  readonly caption: CaptionDisplay | null;
  readonly completion: ActivityHostCompletionMetrics | null;
  readonly feedback: ActivityHostFeedback | null;
  readonly kind: "activity";
  readonly lastInputMethod: InputMethod | null;
  readonly metrics: ActivityCompletionMetrics;
  readonly payload: TPayload;
  readonly phase: ActivePhase;
  readonly renderModel: TRenderModel;
  readonly state: TState;
}

interface ErrorSession {
  readonly attemptNumber: number;
  readonly error: ActivityHostLevelError;
  readonly kind: "error";
  readonly phase: "level-error";
}

type ActivitySessionState<TPayload, TState, TRenderModel> =
  | ActivitySession<TPayload, TState, TRenderModel>
  | ErrorSession;

interface CreateSessionOptions<
  TPayload,
  TState,
  TAction,
  TRenderModel
> {
  readonly adapter: ActivityAdapter<
    TPayload,
    TState,
    TAction,
    TRenderModel
  >;
  readonly attemptNumber: number;
  readonly context: ActivityExecutionContext;
  readonly instruction: ActivityHostInstruction;
  readonly payload: unknown;
  readonly phase: "loading" | "replaying";
}

interface UseActivityHostOptions<
  TPayload,
  TState,
  TAction,
  TRenderModel
> {
  readonly actions?: ActivityHostActionCreators<TAction>;
  readonly adapter: ActivityAdapter<
    TPayload,
    TState,
    TAction,
    TRenderModel
  >;
  readonly bridge: EventBridge<ActivityHostBridgeEvents<TAction>>;
  readonly instruction: ActivityHostInstruction;
  readonly levelId: string;
  readonly nowMs?: () => number;
  readonly onComplete?: (metrics: ActivityHostCompletionMetrics) => void;
  readonly onLevelError?: (error: ActivityHostLevelError) => void;
  readonly payload: unknown;
  readonly reducedMotion?: boolean;
}

export interface ActivityHostController<TAction, TRenderModel> {
  readonly activity: { readonly model: TRenderModel } | null;
  readonly caption: CaptionDisplay | null;
  readonly completion: ActivityHostCompletionMetrics | null;
  readonly error: ActivityHostLevelError | null;
  readonly feedback: ActivityHostFeedback | null;
  readonly getActivationProps: (
    createAction: (inputMethod: InputMethod) => TAction
  ) => ActivityActivationProps;
  readonly metrics: ActivityCompletionMetrics | null;
  readonly pause: (inputMethod: InputMethod) => void;
  readonly phase: ActivityHostPhase;
  readonly reducedMotion: boolean;
  readonly replay: (inputMethod: InputMethod) => void;
  readonly replayNarration: (inputMethod: InputMethod) => void;
  readonly rendererLifecycle: number;
  readonly requestHint: (inputMethod: InputMethod) => void;
  readonly resume: (inputMethod: InputMethod) => void;
  readonly retry: (inputMethod: InputMethod) => void;
  readonly retryLevel: (inputMethod: InputMethod) => void;
  readonly start: (inputMethod: InputMethod) => void;
  readonly dispatch: (action: TAction, inputMethod: InputMethod) => void;
  readonly failRender: () => void;
}

const INVALID_LEVEL_MESSAGE = "这一关还没准备好，先回地图看看吧。";
const ACTIVITY_ERROR_MESSAGE = "这一关刚才停了一下，重新打开就好。";

function getServerBridgeSubscriptionSnapshot(): false {
  return false;
}

function defaultNowMs(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function readNow(nowMs: () => number): number {
  const value = nowMs();
  return Number.isFinite(value) ? value : 0;
}

function getElapsedMs(
  session: ActivitySession<unknown, unknown, unknown>,
  nowMs: () => number
): number {
  const activeSegment =
    session.activeSinceMs === null
      ? 0
      : Math.max(0, readNow(nowMs) - session.activeSinceMs);

  return Math.max(0, Math.round(session.accumulatedActiveMs + activeSegment));
}

function createFallbackCaption(
  instruction: ActivityHostInstruction
): CaptionDisplay | null {
  const text = instruction.caption.trim();

  if (!text) {
    return null;
  }

  return {
    trackId: "activity-instruction",
    text,
    startMs: null,
    endMs: null,
    mode: "fallback"
  };
}

function createErrorSession(
  attemptNumber: number,
  error: ActivityHostLevelError
): ErrorSession {
  return {
    attemptNumber,
    error,
    kind: "error",
    phase: "level-error"
  };
}

function createActivitySession<
  TPayload,
  TState,
  TAction,
  TRenderModel
>({
  adapter,
  attemptNumber,
  context,
  instruction,
  payload,
  phase
}: CreateSessionOptions<
  TPayload,
  TState,
  TAction,
  TRenderModel
>): ActivitySessionState<TPayload, TState, TRenderModel> {
  if (!instruction.caption.trim()) {
    return createErrorSession(attemptNumber, {
      code: "invalid-level",
      message: INVALID_LEVEL_MESSAGE,
      recoverable: false
    });
  }

  try {
    const parsedPayload = adapter.parsePayload(payload);
    const state = adapter.createInitialState(parsedPayload, context);

    return {
      accumulatedActiveMs: 0,
      activeSinceMs: null,
      attemptNumber,
      caption: createFallbackCaption(instruction),
      completion: null,
      feedback: null,
      kind: "activity",
      lastInputMethod: null,
      metrics: adapter.getMetrics(state, parsedPayload),
      payload: parsedPayload,
      phase,
      renderModel: adapter.toRenderModel(state, parsedPayload),
      state
    };
  } catch {
    return createErrorSession(attemptNumber, {
      code: "invalid-level",
      message: INVALID_LEVEL_MESSAGE,
      recoverable: false
    });
  }
}

function toBridgeError(event: ActivityHostBridgeErrorEvent): ActivityHostLevelError {
  return {
    code: event.code ?? "load-failed",
    message: event.message?.trim() || ACTIVITY_ERROR_MESSAGE,
    recoverable: event.recoverable ?? true
  };
}

function createCompletion(
  adapterType: string,
  levelId: string,
  metrics: ActivityCompletionMetrics,
  durationMs: number
): ActivityHostCompletionMetrics {
  return {
    ...metrics,
    activityType: adapterType,
    durationMs,
    levelId
  };
}

export function useActivityHost<
  TPayload,
  TState,
  TAction,
  TRenderModel
>({
  actions,
  adapter,
  bridge,
  instruction,
  levelId,
  nowMs = defaultNowMs,
  onComplete,
  onLevelError,
  payload,
  reducedMotion: reducedMotionOverride
}: UseActivityHostOptions<
  TPayload,
  TState,
  TAction,
  TRenderModel
>): ActivityHostController<TAction, TRenderModel> {
  const reducedMotion = usePrefersReducedMotion(reducedMotionOverride);
  const context = useMemo<ActivityExecutionContext>(
    () => ({ nowMs, reducedMotion }),
    [nowMs, reducedMotion]
  );
  const createSession = useCallback(
    (attemptNumber: number, phase: "loading" | "replaying") =>
      createActivitySession({
        adapter,
        attemptNumber,
        context,
        instruction,
        payload,
        phase
      }),
    [adapter, context, instruction, payload]
  );
  const [session, setSession] = useState<
    ActivitySessionState<TPayload, TState, TRenderModel>
  >(() => createSession(1, "loading"));
  const subscribedBridgeRef = useRef<EventBridge<
    ActivityHostBridgeEvents<TAction>
  > | null>(null);

  const emitCommand = useCallback(
    (command: ActivityHostCommand) => bridge.emit("command", command),
    [bridge]
  );

  const dispatch = useCallback(
    (action: TAction, inputMethod: InputMethod) => {
      setSession((current) => {
        if (current.kind === "error" || current.phase !== "playing") {
          return current;
        }

        try {
          const state = adapter.reduce(
            current.state,
            current.payload,
            action,
            context
          );
          const metrics = adapter.getMetrics(state, current.payload);
          const renderModel = adapter.toRenderModel(state, current.payload);

          if (!metrics.completed) {
            return {
              ...current,
              feedback: null,
              lastInputMethod: inputMethod,
              metrics,
              renderModel,
              state
            };
          }

          const durationMs = getElapsedMs(current, nowMs);

          return {
            ...current,
            accumulatedActiveMs: durationMs,
            activeSinceMs: null,
            completion: createCompletion(
              adapter.activityType,
              levelId,
              metrics,
              durationMs
            ),
            feedback: null,
            lastInputMethod: inputMethod,
            metrics,
            phase: "result",
            renderModel,
            state
          };
        } catch {
          return createErrorSession(current.attemptNumber, {
            code: "activity-failed",
            message: ACTIVITY_ERROR_MESSAGE,
            recoverable: true
          });
        }
      });
    },
    [adapter, context, levelId, nowMs]
  );

  const handleReady = useCallback(
    (event: ActivityHostReadyEvent) => {
      setSession((current) => {
        if (
          current.kind === "error" ||
          (current.phase !== "loading" && current.phase !== "replaying")
        ) {
          return current;
        }

        const phase = current.phase === "loading" ? "tutorial" : "playing";

        return {
          ...current,
          activeSinceMs: phase === "playing" ? readNow(nowMs) : null,
          caption:
            event.caption === undefined ? current.caption : event.caption,
          phase
        };
      });
    },
    [nowMs]
  );

  const handleCaption = useCallback((caption: CaptionDisplay | null) => {
    setSession((current) =>
      current.kind === "error" ? current : { ...current, caption }
    );
  }, []);

  const handleFeedback = useCallback((feedback: ActivityHostFeedback) => {
    setSession((current) =>
      current.kind === "activity" && current.phase === "playing"
        ? { ...current, feedback }
        : current
    );
  }, []);

  const handleBridgeError = useCallback(
    (event: ActivityHostBridgeErrorEvent) => {
      setSession((current) => {
        if (
          current.kind === "error" ||
          current.phase === "result"
        ) {
          return current;
        }

        return createErrorSession(current.attemptNumber, toBridgeError(event));
      });
    },
    []
  );

  const subscribeToBridge = useCallback(
    (onStoreChange: () => void) => {
      const unsubscribe = [
        bridge.subscribe("ready", handleReady),
        bridge.subscribe("action", ({ action, inputMethod }) =>
          dispatch(action, inputMethod)
        ),
        bridge.subscribe("caption", handleCaption),
        bridge.subscribe("feedback", handleFeedback),
        bridge.subscribe("error", handleBridgeError)
      ];

      subscribedBridgeRef.current = bridge;
      onStoreChange();

      return () => {
        if (subscribedBridgeRef.current === bridge) {
          subscribedBridgeRef.current = null;
        }

        for (const stop of unsubscribe) {
          stop();
        }
      };
    },
    [
      bridge,
      dispatch,
      handleBridgeError,
      handleCaption,
      handleFeedback,
      handleReady
    ]
  );
  const getBridgeSubscriptionSnapshot = useCallback(
    () => subscribedBridgeRef.current === bridge,
    [bridge]
  );
  const bridgeSubscriptionReady = useSyncExternalStore(
    subscribeToBridge,
    getBridgeSubscriptionSnapshot,
    getServerBridgeSubscriptionSnapshot
  );

  useEffect(() => {
    emitCommand({
      type: "reduced-motion-changed",
      reducedMotion
    });
  }, [emitCommand, reducedMotion]);

  const completion =
    session.kind === "activity" ? session.completion : null;
  const notifiedCompletion = useRef<ActivityHostCompletionMetrics | null>(null);

  useEffect(() => {
    if (completion === null || notifiedCompletion.current === completion) {
      return;
    }

    notifiedCompletion.current = completion;
    bridge.emit("completion", completion);
    onComplete?.(completion);
  }, [bridge, completion, onComplete]);

  const error = session.kind === "error" ? session.error : null;
  const notifiedError = useRef<ActivityHostLevelError | null>(null);

  useEffect(() => {
    if (error === null || notifiedError.current === error) {
      return;
    }

    notifiedError.current = error;
    onLevelError?.(error);
  }, [error, onLevelError]);

  const start = useCallback(
    (inputMethod: InputMethod) => {
      setSession((current) =>
        current.kind === "activity" && current.phase === "tutorial"
          ? {
              ...current,
              activeSinceMs: readNow(nowMs),
              phase: "playing"
            }
          : current
      );
      emitCommand({ type: "start", inputMethod });
    },
    [emitCommand, nowMs]
  );

  const pause = useCallback(
    (inputMethod: InputMethod) => {
      setSession((current) => {
        if (current.kind === "error" || current.phase !== "playing") {
          return current;
        }

        return {
          ...current,
          accumulatedActiveMs: getElapsedMs(current, nowMs),
          activeSinceMs: null,
          phase: "paused"
        };
      });
      emitCommand({ type: "pause", inputMethod });
    },
    [emitCommand, nowMs]
  );

  const resume = useCallback(
    (inputMethod: InputMethod) => {
      setSession((current) =>
        current.kind === "activity" && current.phase === "paused"
          ? {
              ...current,
              activeSinceMs: readNow(nowMs),
              phase: "playing"
            }
          : current
      );
      emitCommand({ type: "resume", inputMethod });
    },
    [emitCommand, nowMs]
  );

  const requestHint = useCallback(
    (inputMethod: InputMethod) => {
      if (actions?.requestHint) {
        dispatch(actions.requestHint(inputMethod), inputMethod);
      }

      setSession((current) =>
        current.kind === "activity" && current.phase === "playing"
          ? {
              ...current,
              feedback: {
                kind: "hint",
                stage: "gentle-hint"
              }
            }
          : current
      );
      emitCommand({ type: "request-hint", inputMethod });
    },
    [actions, dispatch, emitCommand]
  );

  const retry = useCallback(
    (inputMethod: InputMethod) => {
      if (actions?.retry) {
        dispatch(actions.retry(inputMethod), inputMethod);
      }

      setSession((current) =>
        current.kind === "activity" && current.phase === "playing"
          ? { ...current, feedback: null }
          : current
      );
      emitCommand({ type: "retry", inputMethod });
    },
    [actions, dispatch, emitCommand]
  );

  const replay = useCallback(
    (inputMethod: InputMethod) => {
      setSession((current) => {
        if (current.kind === "error" || current.phase !== "result") {
          return current;
        }

        try {
          const state = actions?.replay
            ? adapter.reduce(
                current.state,
                current.payload,
                actions.replay(inputMethod),
                context
              )
            : adapter.createInitialState(current.payload, context);

          return {
            accumulatedActiveMs: 0,
            activeSinceMs: null,
            attemptNumber: current.attemptNumber + 1,
            caption: createFallbackCaption(instruction),
            completion: null,
            feedback: null,
            kind: "activity",
            lastInputMethod: null,
            metrics: adapter.getMetrics(state, current.payload),
            payload: current.payload,
            phase: "replaying",
            renderModel: adapter.toRenderModel(state, current.payload),
            state
          };
        } catch {
          return createErrorSession(current.attemptNumber + 1, {
            code: "activity-failed",
            message: ACTIVITY_ERROR_MESSAGE,
            recoverable: true
          });
        }
      });
      emitCommand({ type: "replay", inputMethod });
    },
    [actions, adapter, context, emitCommand, instruction]
  );

  const replayNarration = useCallback(
    (inputMethod: InputMethod) => {
      emitCommand({ type: "replay-narration", inputMethod });
    },
    [emitCommand]
  );

  const retryLevel = useCallback(
    (inputMethod: InputMethod) => {
      setSession((current) =>
        current.kind === "error" && current.error.recoverable
          ? createSession(current.attemptNumber + 1, "loading")
          : current
      );
      emitCommand({ type: "retry-level", inputMethod });
    },
    [createSession, emitCommand]
  );

  const failRender = useCallback(() => {
    setSession((current) =>
      current.kind === "error"
        ? current
        : createErrorSession(current.attemptNumber, {
            code: "activity-failed",
            message: ACTIVITY_ERROR_MESSAGE,
            recoverable: true
          })
    );
  }, []);

  const getActivationProps = useCallback(
    (
      createAction: (inputMethod: InputMethod) => TAction
    ): ActivityActivationProps => {
      const onClick: MouseEventHandler<HTMLButtonElement> = (event) => {
        const inputMethod = getActivationInputMethod(event.nativeEvent);
        dispatch(createAction(inputMethod), inputMethod);
      };

      return { onClick };
    },
    [dispatch]
  );

  return {
    activity:
      bridgeSubscriptionReady && session.kind === "activity"
        ? { model: session.renderModel }
        : null,
    caption: session.kind === "activity" ? session.caption : null,
    completion,
    dispatch,
    error,
    failRender,
    feedback: session.kind === "activity" ? session.feedback : null,
    getActivationProps,
    metrics: session.kind === "activity" ? session.metrics : null,
    pause,
    phase: session.phase,
    reducedMotion,
    replay,
    replayNarration,
    rendererLifecycle: session.attemptNumber,
    requestHint,
    resume,
    retry,
    retryLevel,
    start
  };
}
